import {
  GetPlaceCommand,
  GetPlaceCommandOutput,
  LocationClient,
  SearchPlaceIndexForPositionCommand,
  SearchPlaceIndexForTextCommand,
  SearchPlaceIndexForTextCommandOutput,
} from '@aws-sdk/client-location'
import { withAPIKey } from '@aws/amazon-location-utilities-auth-helper'
import { LocationProps, MarkerProps } from 'utils/types'
import MapSchoolIcon from '../images/school_pin.svg'
import MapHomeIcon from '../images/home_pin.svg'

// Singleton instance of AWS LocationClient
let awsLocationClientInstance: LocationClient

const countryCodes: { [country: string]: string } = {
  'United States': 'USA',
  Canada: 'Canada',
  'United Kingdom': 'UK',
  // Add more countries and their codes here
}

export const replaceCountryWithCode = (str: string): string => {
  // Create a regular expression that matches any of the country names at the end of the string
  const regex = new RegExp(`, (${Object.keys(countryCodes).join('|')})$`)

  // Replace the matched country with its code
  return str.replace(regex, (_, country: string) => `, ${countryCodes[country as keyof typeof countryCodes]}`)
}

export const getAwsLocationClient = async () => {
  if (!awsLocationClientInstance) {
    // Create an authentication helper instance using an API key
    const authHelper = await withAPIKey(process.env.REACT_APP_AWS_PLACE_INDEX_KEY || '')

    awsLocationClientInstance = new LocationClient({
      region: 'us-east-1', // Region containing Amazon Location resource
      ...authHelper.getLocationClientConfig(), // Configures the client to use API keys when making supported requests
    })
  }
  return awsLocationClientInstance
}

export const getPlace = async (placeId: string): Promise<LocationProps> => {
  try {
    const awsLocationClient = await getAwsLocationClient()
    const data: GetPlaceCommandOutput = await awsLocationClient.send(
      new GetPlaceCommand({
        IndexName: process.env.REACT_APP_AWS_PLACE_INDEX,
        PlaceId: placeId,
      }),
    )
    if (
      data?.Place &&
      data.Place.Label &&
      data?.Place?.Geometry &&
      data.Place.Geometry.Point?.length &&
      data.Place.Geometry.Point?.length >= 2
    ) {
      const formattedAddress: string = replaceCountryWithCode(data.Place.Label)
      const result: LocationProps = {
        address: formattedAddress,
        lat: data.Place.Geometry.Point[1],
        lng: data.Place.Geometry.Point[0],
        placeId,
      }
      return result
    } else return { address: '' }
  } catch (error) {
    console.error('Error getting place:', error)
    return { address: '' }
  }
}

export const geocode = async (address: string): Promise<LocationProps> => {
  try {
    const awsLocationClient = await getAwsLocationClient()
    const data: SearchPlaceIndexForTextCommandOutput = await awsLocationClient.send(
      new SearchPlaceIndexForTextCommand({
        IndexName: process.env.REACT_APP_AWS_PLACE_INDEX,
        Text: address,
        MaxResults: 1,
      }),
    )
    if (
      data?.Results &&
      data.Results[0] &&
      data.Results[0].Place &&
      data.Results[0].Place.Label &&
      data.Results[0].Place?.Geometry &&
      data.Results[0].Place.Geometry.Point?.length &&
      data.Results[0].Place.Geometry.Point?.length >= 2
    ) {
      const formattedAddress: string = replaceCountryWithCode(data.Results[0].Place.Label)
      const result: LocationProps = {
        address: formattedAddress,
        lat: data.Results[0].Place.Geometry.Point[1],
        lng: data.Results[0].Place.Geometry.Point[0],
        placeId: data.Results[0].PlaceId,
      }
      return result
    } else return { address }
  } catch (error) {
    console.error('Error geocoding address:', error)
    return { address }
  }
}

export const reverseGeocode = async (lat: number, lng: number): Promise<LocationProps> => {
  try {
    const awsLocationClient = await getAwsLocationClient()
    const data = await awsLocationClient.send(
      new SearchPlaceIndexForPositionCommand({
        IndexName: process.env.REACT_APP_AWS_PLACE_INDEX,
        Position: [lng, lat],
        MaxResults: 1,
      }),
    )

    if (
      data?.Results &&
      data.Results[0] &&
      data.Results[0].Place &&
      data.Results[0].Place.Label &&
      data.Results[0].Place?.Geometry &&
      data.Results[0].Place.Geometry.Point?.length &&
      data.Results[0].Place.Geometry.Point?.length >= 2 &&
      data.Results[0].PlaceId
    ) {
      const formattedAddress = replaceCountryWithCode(data.Results[0].Place.Label)
      const result: LocationProps = {
        address: formattedAddress,
        lat: data.Results[0].Place.Geometry.Point[1],
        lng: data.Results[0].Place.Geometry.Point[0],
        placeId: data.Results[0].PlaceId,
      }
      return result
    } else return { address: '' }
  } catch (error) {
    console.error('Error reverse geocoding position:', error)
    return { address: '' }
  }
}

export const getMarker = (
  location: LocationProps,
  label: string,
  radius?: number,
  isDraggable?: boolean,
  iconSize?: { width: number; height: number },
): MarkerProps => {
  const lat: number = location?.lat as number
  const lng: number = location?.lng as number
  const address: string = location?.address as string
  const draggable: boolean = isDraggable ?? false

  // Set zIndex based on the label
  let zIndex: number | undefined
  if (label === 'School') {
    zIndex = 650 // Set zIndex for School marker
  } else if (label === 'You') {
    zIndex = 700 // Set zIndex for Your marker (higher than School)
  }

  if (label === 'School') {
    return {
      position: { lat, lng },
      label,
      iconPath: MapSchoolIcon,
      iconSize: iconSize || { width: 45, height: 45 }, // Default size
      infoWindowContent: `School address is ${address}`,
      draggable,
      zIndex, // Add zIndex property
    }
  } else if (label === 'You') {
    return {
      position: { lat, lng },
      label,
      iconPath: MapHomeIcon,
      iconSize: iconSize || { width: 45, height: 45 }, // Default size
      infoWindowContent: `Your address is ${address}`,
      draggable,
      circleRadius: radius && radius > 0 ? radius : undefined,
      zIndex, // Add zIndex property
    }
  } else {
    return {
      position: { lat, lng },
      draggable,
      zIndex, // Add zIndex property for other markers, if needed
    }
  }
}
